import React, { useMemo, useState } from "react";
import {
  useGetAllUsersQuery,
  useUpdateUserNetworkMutation,
} from "../../services/modules/user";
import { useGetAllNetworksQuery } from "../../services/modules/network";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../store/user";
import { setToken } from "../../store/authSlice";
import { useNavigate } from "react-router-dom";

const UserList = () => {
  const dispatch = useDispatch();
  const { utente: loggedUser } = useSelector((state) => state.user);
  const [updateUserNetwork, { isLoading: isUpdatingNetwork }] =
    useUpdateUserNetworkMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("ALL");
  const [networkSortOrder, setNetworkSortOrder] = useState("NONE");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 50;

  const { data: networksData } = useGetAllNetworksQuery();
  const availableNetworks = networksData?.data?.result || [];

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      pageSize: usersPerPage,
      searchTerm: searchTerm.trim(),
      networkId: selectedNetwork === "ALL" ? "" : selectedNetwork,
      sortBy: networkSortOrder === "NONE" ? "" : "network",
      sortOrder:
        networkSortOrder === "ASC"
          ? "asc"
          : networkSortOrder === "DESC"
          ? "desc"
          : "",
    }),
    [currentPage, usersPerPage, searchTerm, selectedNetwork, networkSortOrder]
  );

  const { data, isLoading, error } = useGetAllUsersQuery(queryParams);
  const users = data?.data?.result || [];
  const totalPages = data?.data?.pagination?.totalPages || 1;
  const navigate = useNavigate();

  const getUserNetworks = (user) => {
    const networkData = user?.network;
    if (!networkData) return [];

    if (Array.isArray(networkData)) {
      return networkData.filter((item) => item && typeof item === "object");
    }

    if (networkData.id && networkData.name) {
      return [networkData];
    }

    return Object.values(networkData).filter(
      (item) => item && typeof item === "object" && item.id && item.name
    );
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Utenti</h2>

      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <div className="w-[28%] min-w-[260px]">
          <input
            type="text"
            placeholder="Cerca in tutti i campi..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="border p-2 w-full rounded"
          />
        </div>
        <div className="min-w-[220px]">
          <label className="block text-sm mb-1">Filtra per rete</label>
          <select
            value={selectedNetwork}
            onChange={(e) => {
              setSelectedNetwork(e.target.value);
              setCurrentPage(1);
            }}
            className="border p-2 rounded w-full"
          >
            <option value="ALL">Tutte le reti</option>
            {availableNetworks.map((network) => (
              <option key={network.id} value={String(network.id)}>
                {network.name}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[220px]">
          <label className="block text-sm mb-1">Ordina per rete</label>
          <select
            value={networkSortOrder}
            onChange={(e) => {
              setNetworkSortOrder(e.target.value);
              setCurrentPage(1);
            }}
            className="border p-2 rounded w-full"
          >
            <option value="NONE">Nessun ordinamento</option>
            <option value="ASC">Rete A-Z</option>
            <option value="DESC">Rete Z-A</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <p>Loading users...</p>
      ) : error ? (
        <p className="text-red-600">Error loading users.</p>
      ) : (
        <>
          <table className="min-w-full bg-white border text-[14px] font-sans ">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-2 py-1">ID</th>
                <th className="border px-2 py-1">Nome/Cognome</th>
                <th className="border px-2 py-1">Email</th>
                <th className="border px-2 py-1">Ruolo</th>
                <th className="border px-2 py-1">Rete</th>
                <th className="border px-2 py-1">Stato</th>
                <th className="border px-2 py-1">Azione</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user) => {
                  const networks = getUserNetworks(user);
                  const selectedNetworkId =
                    networks.find((network) => network.mynetwork)?.id || "";
                  const isLoggedUser =
                    loggedUser?.email && user.email === loggedUser.email;

                  const handleNetworkChange = async (event) => {
                    try {
                      const response = await updateUserNetwork({
                        userId: user.id,
                        networkId: event.target.value,
                      }).unwrap();
                      console.log("updateUserNetwork response", response);

                      const result =
                        response?.data?.result || response?.result || response;
                      const token = result?.token;
                      const updatedUser = result?.user;

                      if (token && updatedUser) {
                        dispatch(setToken(token));
                        dispatch(
                          setUserData({
                            user: updatedUser,
                          })
                        );
                        localStorage.setItem("authToken", token);
                        localStorage.setItem(
                          "userData",
                          JSON.stringify({ user: updatedUser })
                        );
                      }
                    } catch (mutationError) {
                      console.error(
                        "Errore aggiornamento rete utente",
                        mutationError
                      );
                    }
                  };

                  return (
                    <tr key={user.id}>
                      <td className="border px-2 py-1">{user.id}</td>
                      <td className="border px-2 py-1">
                        {user.dislayName || "-"}
                      </td>
                      <td className="border px-2 py-1">{user.email || "-"}</td>
                      <td className="border px-2 py-1">{user.role || "-"}</td>
                      <td className="border px-2 py-1">
                        {isLoggedUser && networks.length ? (
                          <select
                            className="border rounded px-2 py-1 w-full"
                            defaultValue={selectedNetworkId}
                            onChange={handleNetworkChange}
                            disabled={isUpdatingNetwork}
                          >
                            <option value="" disabled>
                              Seleziona rete
                            </option>
                            {networks.map((network) => (
                              <option key={network.id} value={network.id}>
                                {network.name}
                              </option>
                            ))}
                          </select>
                        ) : networks.length ? (
                          networks.find((network) => network.mynetwork)?.name ||
                          networks[0]?.name ||
                          "-"
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="border px-2 py-1 text-left">
                        {user.status === "ACTIVE" ? "Enabled" : "Disabled"}
                      </td>
                      <td className="border px-2 py-1 text-left">
                        <button
                          className="text-red-600 underline"
                          onClick={() => navigate(`/admin/user/${user.id}`)}
                        >
                          Dettagli
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="border text-center py-4">
                    No utenti trovati
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="flex justify-center gap-2 mt-6 flex-wrap">
            {Array.from({ length: totalPages }).map((_, i) => {
              const pageNumber = i + 1;
              const shouldShow =
                pageNumber <= 3 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 &&
                  pageNumber <= currentPage + 1);

              if (
                !shouldShow &&
                (pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2)
              ) {
                return (
                  <span
                    key={i}
                    className="px-3 py-1 bg-gray-100 rounded text-gray-500"
                  >
                    ...
                  </span>
                );
              }

              if (!shouldShow) return null;

              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`px-3 py-1 rounded ${
                    currentPage === pageNumber
                      ? "bg-gray-800 text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default UserList;
